import type { ActionRecordOutputs, CanvasBatchUploadResponse, CanvasScope } from '@/types/canvas';
import type { EpisodeListResponse } from '@/types/episode';
import type {
  BatchDeleteNodesResponse,
  BatchNodeReferencesResponse,
  BatchUpdateNodeItem,
  BatchUpdateNodesResponse,
  CanvasUploadResult,
  DownloadUrlResponse,
  NodeActionRecord,
  NodeListResponse,
  NodeReferencesResponse,
  NodeResponse,
  SaveAssetNodeResponse,
  StatsResponse,
  SummaryResponse,
  UploadFileResponse,
  UploadListResponse,
} from '@/types/graph';

import { request, type UploadProgressOptions, uploadWithProgress } from './client';

function normalizeNodeReferencesResponse(
  response: BatchNodeReferencesResponse | NodeReferencesResponse,
): BatchNodeReferencesResponse {
  if (isBatchNodeReferencesResponse(response)) {
    return response;
  }
  return {
    references: [response],
    not_found_node_keys: [],
  };
}

function isBatchNodeReferencesResponse(
  response: BatchNodeReferencesResponse | NodeReferencesResponse,
): response is BatchNodeReferencesResponse {
  return 'references' in response && Array.isArray(response.references);
}

function emptyNodeReferences(nodeKey: string): NodeReferencesResponse {
  return {
    node_key: nodeKey,
    node_name: null,
    is_referenced: false,
    referencing_storyboards: [],
    referencing_scripts: [],
    referencing_videos: [],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isBatchUpdateNodeItem(value: unknown): value is BatchUpdateNodeItem {
  return (
    isRecord(value) && typeof value.node_key === 'string' && typeof value.node_name === 'string'
  );
}

function getBatchUpdateNodes(response: BatchUpdateNodesResponse): BatchUpdateNodeItem[] {
  if (Array.isArray(response.updated_nodes)) {
    return response.updated_nodes.filter(isBatchUpdateNodeItem);
  }
  if (Array.isArray(response.nodes)) {
    return response.nodes.filter(isBatchUpdateNodeItem);
  }
  return [];
}

function resolveRenamedNode(
  response: BatchUpdateNodesResponse,
  nodeKey: string,
  nodeName: string,
): BatchUpdateNodeItem {
  const targetError = response.errors?.find((error) => error.node_key === nodeKey);
  if (targetError) {
    throw new Error(targetError.message || '节点改名失败');
  }

  if (response.not_found_node_keys?.includes(nodeKey)) {
    throw new Error(`节点不存在或已删除：${nodeKey}`);
  }

  const updatedNode = getBatchUpdateNodes(response).find((node) => node.node_key === nodeKey);
  if (updatedNode) {
    return {
      node_key: updatedNode.node_key,
      node_name: updatedNode.node_name.trim(),
    };
  }

  if (response.updated_node_keys?.includes(nodeKey)) {
    return { node_key: nodeKey, node_name: nodeName };
  }

  throw new Error(`节点改名失败：后端未返回目标节点 ${nodeKey} 的更新结果`);
}

function normalizeNodeKey(nodeKey: string): string {
  const trimmedNodeKey = nodeKey.trim();
  if (!trimmedNodeKey) {
    throw new Error('节点标识不能为空');
  }
  try {
    encodeURIComponent(trimmedNodeKey);
  } catch {
    throw new Error('节点标识格式不正确');
  }
  return trimmedNodeKey;
}

function checkNodeReferences(nodeKey: string): Promise<NodeReferencesResponse>;
function checkNodeReferences(nodeKeys: string[]): Promise<BatchNodeReferencesResponse>;
async function checkNodeReferences(
  nodeKeys: string | string[],
): Promise<NodeReferencesResponse | BatchNodeReferencesResponse> {
  const isBatchRequest = Array.isArray(nodeKeys);
  const dedupedNodeKeys = Array.from(
    new Set((isBatchRequest ? nodeKeys : [nodeKeys]).filter(Boolean)),
  );
  if (dedupedNodeKeys.length === 0) {
    return isBatchRequest
      ? { references: [], not_found_node_keys: [] }
      : emptyNodeReferences(typeof nodeKeys === 'string' ? nodeKeys : '');
  }

  if (!isBatchRequest) {
    const response = await request<NodeReferencesResponse | BatchNodeReferencesResponse>(
      `/graph/nodes/${encodeURIComponent(dedupedNodeKeys[0])}/references`,
    );
    if (isBatchNodeReferencesResponse(response)) {
      return response.references[0] ?? emptyNodeReferences(dedupedNodeKeys[0]);
    }
    return response;
  }

  const query = new URLSearchParams();
  dedupedNodeKeys.forEach((nodeKey) => query.append('node_keys', nodeKey));
  const response = await request<BatchNodeReferencesResponse | NodeReferencesResponse>(
    `/graph/nodes/${encodeURIComponent(dedupedNodeKeys[0])}/references?${query.toString()}`,
  );
  return normalizeNodeReferencesResponse(response);
}

export const GraphApi = {
  // 查询接口
  getStats(projectKey: string): Promise<StatsResponse> {
    return request(`/graph/stats?project_key=${encodeURIComponent(projectKey)}`);
  },

  getSummary(projectKey: string, previewSize = 100): Promise<SummaryResponse> {
    const query = new URLSearchParams({
      project_key: projectKey,
      preview_size: String(previewSize),
    });
    return request(`/graph/summary?${query.toString()}`);
  },

  listNodes(params: {
    project_key: string;
    tab_type?: string;
    status?: string;
    keyword?: string;
    page?: number;
    page_size?: number;
  }): Promise<NodeListResponse> {
    const query = new URLSearchParams();
    query.set('project_key', params.project_key);
    if (params.tab_type) query.set('tab_type', params.tab_type);
    if (params.status) query.set('status', params.status);
    if (params.keyword) query.set('keyword', params.keyword);
    if (params.page) query.set('page', String(params.page));
    if (params.page_size) query.set('page_size', String(params.page_size));
    return request(`/graph/nodes?${query.toString()}`);
  },

  getNode(nodeKey: string, withNeighbors = false): Promise<NodeResponse> {
    const q = withNeighbors ? '?with_neighbors=true' : '';
    return request(`/graph/nodes/${encodeURIComponent(nodeKey)}${q}`);
  },

  createDownloadUrl(nodeKey: string, recordId: number): Promise<DownloadUrlResponse> {
    return request(`/graph/nodes/${encodeURIComponent(nodeKey)}/records/${recordId}/download-url`, {
      method: 'POST',
    });
  },

  /**
   * 剧集视图（两阶段加载）。
   *
   * - 不传 episodeNodeKey：返回剧集列表摘要（倒序）+ 第一个剧集的完整镜头数据
   * - 传入 episodeNodeKey：返回指定剧集的完整镜头详情（单集）
   */
  listEpisodes(projectKey: string, episodeNodeKey?: string): Promise<EpisodeListResponse> {
    const params = new URLSearchParams({ project_key: projectKey });
    if (episodeNodeKey) params.set('episode_node_key', episodeNodeKey);
    return request(`/graph/episodes?${params.toString()}`);
  },

  getNeighbors(
    nodeKey: string,
    direction?: string,
    nodeTypes?: string,
  ): Promise<{ node_key: string; direction: string; neighbors: NodeResponse[] }> {
    const query = new URLSearchParams();
    if (direction) query.set('direction', direction);
    if (nodeTypes) query.set('node_types', nodeTypes);
    return request(`/graph/nodes/${encodeURIComponent(nodeKey)}/neighbors?${query.toString()}`);
  },

  listUploads(
    projectKey: string,
    page = 1,
    pageSize = 1000,
    mediaType?: number,
  ): Promise<UploadListResponse> {
    const query = new URLSearchParams({
      project_key: projectKey,
      page: String(page),
      page_size: String(pageSize),
    });
    if (mediaType !== undefined) query.set('media_type', String(mediaType));
    return request(`/file/uploads?${query.toString()}`);
  },

  // 写入接口
  createNode(payload: {
    project_key: string;
    biz_type: number;
    media_type: number;
    node_name: string;
    position?: Record<string, string>;
  }): Promise<NodeResponse> {
    return request('/graph/nodes', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  deleteNode(nodeKey: string): Promise<void> {
    return request(`/graph/nodes/${encodeURIComponent(nodeKey)}`, {
      method: 'DELETE',
    });
  },

  checkNodeReferences,

  batchDeleteNodes(nodeKeys: string[]): Promise<BatchDeleteNodesResponse> {
    const dedupedNodeKeys = Array.from(new Set(nodeKeys.filter(Boolean)));
    if (dedupedNodeKeys.length === 0) {
      return Promise.resolve({ deleted_node_keys: [], not_found_node_keys: [] });
    }

    return request('/graph/nodes/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ node_keys: dedupedNodeKeys }),
    });
  },

  async renameNode(nodeKey: string, nodeName: string): Promise<BatchUpdateNodeItem> {
    const trimmedNodeKey = normalizeNodeKey(nodeKey);
    const trimmedNodeName = nodeName.trim();
    if (!trimmedNodeName) {
      throw new Error('节点名称不能为空');
    }

    const response = await request<BatchUpdateNodesResponse>('/graph/nodes/batch', {
      method: 'PATCH',
      body: JSON.stringify({
        updates: [
          {
            node_key: trimmedNodeKey,
            node_name: trimmedNodeName,
          },
        ],
      }),
    });
    return resolveRenamedNode(response, trimmedNodeKey, trimmedNodeName);
  },

  createActionRecords(payload: {
    node_key: string;
    inputs: Record<string, unknown>;
    task_info?: Record<string, unknown>;
    status?: string;
  }): Promise<NodeActionRecord[]> {
    return request('/graph/node-action-records', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateActionRecord(
    id: number,
    payload: {
      inputs?: Record<string, unknown>;
      task_info?: Record<string, unknown>;
      outputs?: Record<string, unknown>;
      status?: string;
      valided?: 0 | 1;
    },
  ): Promise<NodeActionRecord> {
    return request(`/graph/node-action-records/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /**
   * 按 node_key 批量更新所有 records 的 association（角色级别共享音频配置）。
   */
  updateNodeAssociation(
    nodeKey: string,
    association: Array<{
      media_type: number;
      node_name?: string;
      node_key: string;
      uri?: string;
      node_action_record_id: number;
    }>,
  ): Promise<{ ok: boolean }> {
    return request(`/graph/nodes/${nodeKey}/association`, {
      method: 'PATCH',
      body: JSON.stringify({ association }),
    });
  },

  /**
   * 切换节点生效行为记录版本。
   *
   * 原子操作：将 node_key 下所有记录 valided 置 0，再将 record_id 对应记录 valided 置 1。
   */
  switchActiveRecord(nodeKey: string, recordId: number): Promise<NodeActionRecord> {
    return request('/graph/node-action-records/switch-active', {
      method: 'POST',
      body: JSON.stringify({ node_key: nodeKey, record_id: recordId }),
    });
  },

  createNodeWithActionRecord(payload: {
    project_key: string;
    biz_type: number;
    media_type: number;
    node_name: string;
    measure?: { x: number; y: number; width?: number; height?: number };
    inputs?: Record<string, unknown>;
    task_info?: Record<string, unknown>;
    status?: string;
    count?: number;
  }): Promise<{ node: NodeResponse; action_records: NodeActionRecord[] }> {
    return request('/graph/nodes/with-action-record', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  saveAssetNode(payload: {
    user_id: number;
    project_key: string;
    biz_type: number;
    media_type?: number;
    content: string | Record<string, unknown>;
    node_name?: string;
    inputs?: Record<string, unknown>;
  }): Promise<SaveAssetNodeResponse> {
    return request('/graph/nodes/save-asset', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // 上传接口
  uploadText(
    projectKey: string,
    file: File,
    options?: UploadProgressOptions,
    canvasScope?: CanvasScope,
  ): Promise<UploadFileResponse> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('file', file);
    if (canvasScope !== undefined) {
      fd.append('canvas_upload', 'true');
      if (canvasScope.conversationKey) fd.append('conversation_key', canvasScope.conversationKey);
      if (canvasScope.storyboardNodeKey)
        fd.append('storyboard_node_key', canvasScope.storyboardNodeKey);
    }
    return uploadWithProgress('/file/uploads/text', fd, options);
  },

  uploadImage(projectKey: string, file: File): Promise<UploadFileResponse> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('file', file);
    return request('/file/uploads/image', { method: 'POST', body: fd });
  },

  uploadVideo(
    projectKey: string,
    file: File,
    options?: UploadProgressOptions,
    canvasScope?: CanvasScope,
  ): Promise<UploadFileResponse> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('file', file);
    if (canvasScope !== undefined) {
      fd.append('canvas_upload', 'true');
      if (canvasScope.conversationKey) fd.append('conversation_key', canvasScope.conversationKey);
      if (canvasScope.storyboardNodeKey)
        fd.append('storyboard_node_key', canvasScope.storyboardNodeKey);
    }
    return uploadWithProgress('/file/uploads/video', fd, options);
  },

  uploadCanvasFiles(
    projectKey: string,
    mode: 'files' | 'zip',
    files: File[],
    options?: UploadProgressOptions,
    canvasScope?: CanvasScope,
  ): Promise<CanvasBatchUploadResponse> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('mode', mode);
    for (const file of files) fd.append('files', file);
    if (canvasScope?.conversationKey) {
      fd.append('conversation_key', canvasScope.conversationKey);
    }
    if (canvasScope?.storyboardNodeKey) {
      fd.append('storyboard_node_key', canvasScope.storyboardNodeKey);
    }
    return uploadWithProgress('/graph/uploads/canvas', fd, options);
  },

  uploadZipImages(
    projectKey: string,
    assetType: number,
    file: File,
    options?: UploadProgressOptions,
  ): Promise<UploadFileResponse[]> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('asset_type', String(assetType));
    fd.append('file', file);
    return uploadWithProgress('/graph/uploads/zip-images', fd, options);
  },

  uploadZipAudios(
    projectKey: string,
    file: File,
    options?: UploadProgressOptions,
  ): Promise<UploadFileResponse[]> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('file', file);
    return uploadWithProgress('/graph/uploads/zip-audios', fd, options);
  },

  /**
   * 混合批量上传资产（zip + 散文件）—— 三按钮统一入口。
   *
   * 支持文件类型由后端按 assetType 判定：
   * - assetType=9 → 音频模式，仅 .zip / .mp3/.wav/.aac/.flac/.ogg/.m4a/.wma
   * - 其他      → 图片模式，仅 .zip / .png/.jpg/.jpeg/.webp/.gif/.bmp
   *
   * 总大小上限 2GB（前后端都会校验）。
   */
  uploadBatchAssets(
    projectKey: string,
    assetType: number,
    files: File[],
    options?: UploadProgressOptions,
    canvasScope?: CanvasScope,
  ): Promise<CanvasUploadResult[]> {
    const fd = new FormData();
    fd.append('project_key', projectKey);
    fd.append('asset_type', String(assetType));
    for (const file of files) {
      fd.append('files', file);
    }
    if (canvasScope !== undefined) {
      fd.append('canvas_upload', 'true');
      if (canvasScope.conversationKey) fd.append('conversation_key', canvasScope.conversationKey);
      if (canvasScope.storyboardNodeKey)
        fd.append('storyboard_node_key', canvasScope.storyboardNodeKey);
    }
    return uploadWithProgress('/graph/uploads/batch-assets', fd, options);
  },

  /**
   * 上传单个音频文件（复用 batch-assets 端点，仅传 1 个文件）。
   *
   * 注意：batch-assets 后端实际返回 ZipAssetUploadResultItem（{ node, action_record }），
   * 真实音频地址在 action_record.outputs.result[0].uri（协议字段为 uri，部分链路回填为 url），
   * 并非 UploadFileResponse 的顶层 oss_uri/url。这里统一提取并回填到 url 字段，
   * 使调用方可直接读取 uploaded.url 拿到可访问地址。
   */
  async uploadSingleAudioFile(
    projectKey: string,
    file: File,
    options?: UploadProgressOptions,
  ): Promise<UploadFileResponse & { audio_node_key?: string; node_action_record_id?: number }> {
    const results = await this.uploadBatchAssets(projectKey, 9, [file], options);
    const first = results[0] as UploadFileResponse & {
      node?: { node_key?: string };
      action_record?: { id?: number; outputs?: ActionRecordOutputs | null };
    };
    const firstResult = first?.action_record?.outputs?.result?.[0] as
      { uri?: string; url?: string } | string | undefined;
    const audioUri =
      typeof firstResult === 'string' ? firstResult : (firstResult?.uri ?? firstResult?.url);
    return {
      ...first,
      url: audioUri || first?.url || first?.oss_uri || '',
      audio_node_key: first?.node?.node_key,
      node_action_record_id: first?.action_record?.id,
    };
  },

  /**
   * Canvas compatibility entry for storyboard output sync.
   * Knowledge/storyboard panel editing should use storyboardApi.updateStoryboard.
   */
  updateStoryboard(
    storyboardNodeKey: string,
    payload: {
      project_key: string;
      node_name?: string;
      episode_no?: number;
      episode_title?: string;
      shots: Array<{
        shot_index: number;
        references?: Array<{
          node_key?: string | null;
          node_name?: string | null;
          uri?: string | null;
          media_type?: number | null;
          node_action_record_id?: number | null;
        }> | null;
        video_prompt?: string;
      }>;
    },
  ): Promise<{
    node_key: string;
    project_key: string;
    biz_type: number;
    media_type: number;
    node_name: string;
    action_record_id: number;
    outputs: ActionRecordOutputs | null;
  }> {
    return request(`/graph/storyboards/${encodeURIComponent(storyboardNodeKey)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};
